// src/pages/cajaPage.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Tabs, Button, Tooltip, Row, Col, Card, Statistic, Badge, Space,
    Typography, Tag, Spin, Modal, InputNumber, Alert, Divider,
    Table, DatePicker, message, Empty, Segmented
} from 'antd';
import {
    DollarOutlined, CreditCardOutlined, ReloadOutlined, CalendarOutlined,
    ToolOutlined, PlayCircleOutlined, LogoutOutlined, MinusCircleOutlined,
    CheckCircleOutlined, LockOutlined,
    DisconnectOutlined, DatabaseOutlined, WarningOutlined, WifiOutlined,
    CloudServerOutlined, EyeOutlined, PrinterOutlined, CloseOutlined,
    ArrowUpOutlined, ArrowDownOutlined, BankOutlined, SwapOutlined
} from '@ant-design/icons';
import operationsService from '../services/operationsService';
import analyticsV2Service from '../services/analyticsV2Service';
import CashWithdrawal from '../components/operation/CashWithdrawal';
import OperationReport from '../components/operation/OperationReport';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import './cajaPage.css';

const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';

dayjs.extend(relativeTime);
dayjs.extend(utc);

const { Text } = Typography;
const { RangePicker } = DatePicker;

// ─── Cache config ────────────────────────────────────────────────────────────
const CACHE_KEY = 'dailyOperationsCache';
const CACHE_DURATION = 5 * 60 * 1000;
const SYNC_INTERVAL = 30 * 1000;

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmt = (n) =>
    typeof n === 'number' && !isNaN(n)
        ? `$ ${n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : 'N/A';

const fmtShort = (n) => {
    if (typeof n !== 'number' || isNaN(n)) return '$0';
    const abs = Math.abs(n);
    if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `$${(n / 1_000).toFixed(0)}k`;
    return `$${n.toFixed(0)}`;
};

const PM_COLORS = {
    '201': '#52c41a', '202': '#ff4d4f', '203': '#722ed1',
    '204': '#faad14', '205': '#13c2c2', '206': '#eb2f96',
    '207': '#1890ff', '208': '#8c8c8c',
};
const PM_NAMES = {
    '201': 'Contado', '202': 'Cta. Cte.', '203': 'Cheque',
    '204': 'T. Crédito', '205': 'T. Débito', '206': 'Transferencia',
    '207': 'Mercado Pago', '208': 'Otra',
};

// ─── Movement helpers (inlined from MovementsList) ───────────────────────────
const getMovementTag = (type, reason) => {
    if (type === 'income') {
        if (reason === 'ar_payment') return <Tag color="green" style={{ margin: 0 }}>💰 Pago CC</Tag>;
        const depositMap = { change_fund: '💵 Fondo cambio', petty_cash: '🏧 Caja chica', reimbursement: '🔄 Reintegro', bank_withdrawal: '🏦 Extracción', other: 'Ingreso manual' };
        return <Tag color="green" style={{ margin: 0 }}>{depositMap[reason] || 'Ingreso manual'}</Tag>;
    }
    if (type === 'payment') return <Tag color="cyan" style={{ margin: 0 }}>Pago</Tag>;
    if (type === 'withdrawal') {
        const map = { security: ['red', 'Seguridad'], supplier_payment: ['orange', 'Pago Prov.'], expense: ['gold', 'Gasto'], bank_deposit: ['blue', 'Depósito'], other: ['default', 'Otro'] };
        const [color, text] = map[reason] || ['default', reason || 'Retiro'];
        return <Tag color={color} style={{ margin: 0 }}>{text}</Tag>;
    }
    if (type === 'expense') return <Tag color="gold" style={{ margin: 0 }}>Gasto</Tag>;
    if (type === 'adjustment') return <Tag color="purple" style={{ margin: 0 }}>Ajuste</Tag>;
    return <Tag color="blue" style={{ margin: 0 }}>{type || 'Mov.'}</Tag>;
};

const getReferenceTag = (refType, refId) => {
    if (!refType) return null;
    const cfg = {
        ORDER: { color: 'blue', label: 'Orden', link: null },
        INVOICE: { color: 'green', label: 'Factura', link: `#/print-quote?invoiceId=${refId}` },
        CREDIT_NOTE: { color: 'orange', label: 'NC', link: '#/notas-credito' },
        MANUAL: { color: 'default', label: 'Manual', link: null },
    };
    const { color, label, link } = cfg[refType] || { color: 'default', label: refType, link: null };
    if (link && refId) {
        return <a href={link} target="_blank" rel="noopener noreferrer"><Tag color={color} style={{ margin: 0, cursor: 'pointer' }}>#{refId}</Tag></a>;
    }
    return <Tag color={color} style={{ margin: 0 }}>{label}</Tag>;
};

// ─── CAJA DIARIA TAB ─────────────────────────────────────────────────────────
function CajaDiariaTab({ reloadKey }) {
    // ── Caja State ──
    const [isDayStarted, setIsDayStarted] = useState(false);
    const [initialCash, setInitialCash] = useState(0);
    const [tempInitialCash, setTempInitialCash] = useState(0);
    const [startDayModalVisible, setStartDayModalVisible] = useState(false);
    const [dayStartTime, setDayStartTime] = useState(null);
    const [openedBy, setOpenedBy] = useState(null);
    const [operationId, setOperationId] = useState(null);

    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [lastSyncTime, setLastSyncTime] = useState(null);
    const [dataSource, setDataSource] = useState(null);
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [syncError, setSyncError] = useState(null);
    const [showDataSourceAlert, setShowDataSourceAlert] = useState(false);

    const [withdrawalModalVisible, setWithdrawalModalVisible] = useState(false);
    const [closeDayModalVisible, setCloseDayModalVisible] = useState(false);
    const [closingSummary, setClosingSummary] = useState(null);
    const [loadingSummary, setLoadingSummary] = useState(false);
    const [modalTotalCounted, setModalTotalCounted] = useState(null);
    const [modalDifference, setModalDifference] = useState(null);

    // ── Sales Data State (lifted from VentasDelDia) ──
    const [salesLoading, setSalesLoading] = useState(true);
    const [cashData, setCashData] = useState({ totalAmount: 0, invoiceCount: 0 });
    const [otherPaymentMethods, setOtherPaymentMethods] = useState([]);

    // ── Movements State ──
    const [movements, setMovements] = useState([]);
    const [movementsLoading, setMovementsLoading] = useState(false);

    const syncIntervalRef = useRef(null);

    // ── Cache helpers ──
    const saveToCache = useCallback((data) => {
        try { localStorage.setItem(CACHE_KEY, JSON.stringify({ ...data, cachedAt: Date.now(), expiresAt: Date.now() + CACHE_DURATION })); } catch (_) {}
    }, []);
    const loadFromCache = useCallback(() => {
        try { const r = localStorage.getItem(CACHE_KEY); return r ? JSON.parse(r) : null; } catch (_) { return null; }
    }, []);
    const isCacheExpired = useCallback((d) => !d || Date.now() > d.expiresAt, []);
    const clearCache = useCallback(() => { try { localStorage.removeItem(CACHE_KEY); } catch (_) {} }, []);

    // ── Appliers ──
    const applyOperationState = useCallback((data) => {
        if (data && data.status === 'open') {
            setOperationId(data.operationId); setIsDayStarted(true);
            setInitialCash(data.initialCash || 0); setDayStartTime(data.openedAt); setOpenedBy(data.openedBy);
        } else {
            setOperationId(null); setIsDayStarted(false); setInitialCash(0); setDayStartTime(null); setOpenedBy(null);
        }
    }, []);
    const clearOperationState = useCallback(() => {
        setOperationId(null); setIsDayStarted(false); setInitialCash(0); setDayStartTime(null); setOpenedBy(null);
    }, []);

    // ── Load Caja State ──
    const loadOperationState = useCallback(async () => {
        setLoading(true); setSyncError(null);
        try {
            const cached = loadFromCache();
            if (cached && !isCacheExpired(cached)) { applyOperationState(cached); setDataSource('cache'); setLastSyncTime(cached.cachedAt); setShowDataSourceAlert(true); }
            if (!isOnline) throw new Error('OFFLINE');
            const res = await operationsService.getCurrentOperation();
            if (res.success && res.data) { applyOperationState(res.data); saveToCache(res.data); setDataSource('backend'); setLastSyncTime(Date.now()); setShowDataSourceAlert(false); }
            else { clearOperationState(); clearCache(); setDataSource('backend'); setLastSyncTime(Date.now()); }
        } catch (err) {
            setSyncError(err.message);
            if (!isOnline || err.message === 'OFFLINE' || err.code === 'ERR_NETWORK') {
                const cached = loadFromCache();
                if (cached) { applyOperationState(cached); setDataSource('offline'); setLastSyncTime(cached.cachedAt); setShowDataSourceAlert(true); }
                else setDataSource('offline');
            } else { message.error('Error al cargar estado de la caja'); }
        } finally { setLoading(false); }
    }, [isOnline, loadFromCache, isCacheExpired, applyOperationState, clearOperationState, saveToCache, clearCache]);

    // ── Load Sales Data ──
    const loadSalesData = useCallback(async () => {
        setSalesLoading(true);
        try {
            const today = dayjs().format('YYYY-MM-DD');
            const res = await analyticsV2Service.getPaymentMethods(today, today);
            const methods = res.data?.paymentMethods || [];
            const cash = methods.find(m => m.code === '201');
            setCashData({ totalAmount: cash?.totalAmount || 0, invoiceCount: cash?.count || 0 });
            setOtherPaymentMethods(methods.filter(m => m.code !== '201' && m.count > 0));
        } catch (_) {
            setCashData({ totalAmount: 0, invoiceCount: 0 });
            setOtherPaymentMethods([]);
        } finally { setSalesLoading(false); }
    }, []);

    // ── Load Movements ──
    const loadMovementsData = useCallback(async () => {
        setMovementsLoading(true);
        try {
            const res = await operationsService.getMovements();
            setMovements(res.data || []);
        } catch (err) {
            if (err.response?.status !== 404) console.error('Error loading movements:', err);
            setMovements([]);
        } finally { setMovementsLoading(false); }
    }, []);

    // ── Online / Offline ──
    useEffect(() => {
        const onOnline = () => { setIsOnline(true); loadOperationState(); };
        const onOffline = () => { setIsOnline(false); message.warning('Sin conexión - modo offline'); };
        window.addEventListener('online', onOnline);
        window.addEventListener('offline', onOffline);
        return () => { window.removeEventListener('online', onOnline); window.removeEventListener('offline', onOffline); };
    }, [loadOperationState]);

    // ── Mount / reload ──
    useEffect(() => {
        loadOperationState();
        loadSalesData();
    }, [reloadKey]); // eslint-disable-line

    // ── Load movements when day starts ──
    useEffect(() => {
        if (isDayStarted) loadMovementsData();
    }, [isDayStarted, loadMovementsData]);

    // ── Auto-sync polling ──
    useEffect(() => {
        if (isDayStarted && isOnline) {
            syncIntervalRef.current = setInterval(async () => {
                if (syncing) return;
                setSyncing(true);
                try {
                    const res = await operationsService.getCurrentOperation();
                    if (res.success && res.data) { saveToCache(res.data); setDataSource('backend'); setLastSyncTime(Date.now()); setSyncError(null); }
                } catch (_) {} finally { setSyncing(false); }
            }, SYNC_INTERVAL);
            return () => clearInterval(syncIntervalRef.current);
        }
    }, [isDayStarted, isOnline, syncing, saveToCache]);

    // ── Action handlers ──
    const handleStartDay = () => {
        setTempInitialCash(initialCash);
        setStartDayModalVisible(true);
    };

    const handleConfirmStartDay = async () => {
        setLoading(true);
        try {
            const res = await operationsService.startDay({ initialCash: tempInitialCash, openingNotes: 'Operación iniciada desde el sistema' });
            if (res.success && res.data) {
                applyOperationState(res.data); saveToCache(res.data);
                setDataSource('backend'); setLastSyncTime(Date.now());
                setStartDayModalVisible(false);
                message.success('✅ Día iniciado exitosamente');
            }
        } catch (err) {
            if (!isOnline || err.response?.status === 404 || err.code === 'ERR_NETWORK') {
                const offline = { operationId: `offline-${Date.now()}`, status: 'open', initialCash: tempInitialCash, openedAt: dayjs().toISOString(), openedBy: 'Usuario Local' };
                applyOperationState(offline); saveToCache(offline); setDataSource('offline');
                setStartDayModalVisible(false);
                message.warning({ content: '⚠️ Día iniciado en modo offline', duration: 5 });
            } else { message.error('Error al iniciar el día'); }
        } finally { setLoading(false); }
    };

    const handleCloseDay = async () => {
        setModalTotalCounted(null); setModalDifference(null); setClosingSummary(null);
        setLoadingSummary(true); setCloseDayModalVisible(true);
        try {
            const res = await operationsService.getClosingSummary();
            if (res.success && res.data) setClosingSummary(res.data);
            else message.warning('No se pudo cargar el resumen de cierre');
        } catch (_) { message.error('Error al cargar el resumen de cierre'); }
        finally { setLoadingSummary(false); }
    };

    const handleConfirmCloseDay = async () => {
        if (modalTotalCounted === null || modalTotalCounted === undefined) { message.warning('Ingrese el total contado'); return; }
        setLoading(true);
        try {
            const res = await operationsService.closeDay({ countedCash: modalTotalCounted, closingNotes: modalDifference !== null ? `Diferencia: $${modalDifference.toFixed(2)}` : null });
            clearOperationState(); clearCache(); setCloseDayModalVisible(false); setDataSource('backend');
            if (res.data?.operationDate) {
                message.success({ content: <span>✅ Caja cerrada. <a href={`#/operation-report/${res.data.operationDate}`} style={{ marginLeft: 8, textDecoration: 'underline' }} onClick={() => message.destroy()}>Ver informe</a></span>, duration: 10 });
            } else { message.success('✅ Caja cerrada exitosamente'); }
        } catch (err) {
            if (!isOnline || err.response?.status === 404 || err.code === 'ERR_NETWORK') {
                clearOperationState(); clearCache(); setCloseDayModalVisible(false);
                message.warning({ content: '⚠️ Caja cerrada localmente', duration: 5 });
            } else { message.error('❌ Error al cerrar la caja'); }
        } finally { setLoading(false); }
    };

    // ── Derived ──
    const syncStatus = syncing ? 'processing' : dataSource === 'backend' ? 'success' : dataSource === 'offline' ? 'error' : 'warning';
    const syncLabel = syncing ? 'Sincronizando...' : dataSource === 'backend' ? 'En línea' : dataSource === 'offline' ? 'Offline' : 'Caché';
    const otherMethodsTotal = otherPaymentMethods.reduce((s, m) => s + m.totalAmount, 0);
    const otherMethodsCount = otherPaymentMethods.reduce((s, m) => s + m.count, 0);
    const movementsNetTotal = movements.reduce((sum, m) => {
        const type = m.movementType || m.type || '';
        const amount = Math.abs(m.amount);
        if (type === 'income' || type === 'payment') return sum + amount;
        if (type === 'withdrawal' || type === 'expense') return sum - amount;
        return sum;
    }, 0);

    // ── Movements table columns ──
    const movementColumns = [
        {
            title: 'Hora', key: 'ts', width: 64,
            render: (_, r) => {
                const ts = r.movementTimestamp || r.timestamp || r.movement_timestamp || r.created_at || r.createdAt;
                return <Text style={{ fontFamily: 'monospace', fontSize: 13, color: '#666' }}>{ts ? dayjs(ts).format('HH:mm') : '--:--'}</Text>;
            },
        },
        {
            title: 'Tipo', key: 'type', width: 120,
            render: (_, r) => getMovementTag(r.movementType || r.type || '', r.reason || ''),
        },
        {
            title: 'Origen', key: 'ref', width: 90,
            render: (_, r) => getReferenceTag(r.reference_type || r.referenceType, r.reference_id || r.referenceId),
        },
        {
            title: 'Descripción', dataIndex: 'description', key: 'desc', ellipsis: true,
            render: (d) => <Text style={{ fontSize: 13 }}>{d || '—'}</Text>,
        },
        {
            title: 'Monto', key: 'amount', width: 130, align: 'right',
            render: (_, r) => {
                const type = r.movementType || r.type || '';
                const isIncome = type === 'income' || type === 'payment';
                const amount = Math.abs(r.amount);
                return (
                    <Text strong style={{ color: isIncome ? '#52c41a' : '#ff4d4f', fontSize: 14 }}>
                        {isIncome ? '+' : '−'} ${amount.toLocaleString('es-AR', { minimumFractionDigits: 0 })}
                    </Text>
                );
            },
        },
    ];

    if (loading && dataSource === null) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                <Spin size="large" tip="Cargando estado de la caja..." />
            </div>
        );
    }

    return (
        <>
            {/* ── Sync / offline alerts ── */}
            {!isOnline && <Alert type="warning" message="Sin conexión a Internet" description="Trabajando en modo offline." icon={<DisconnectOutlined />} showIcon closable style={{ marginBottom: 12 }} />}
            {showDataSourceAlert && dataSource === 'cache' && <Alert type="info" message="Usando datos en caché" description={`Cargados ${dayjs(lastSyncTime).fromNow()}. Sincronizando...`} icon={<DatabaseOutlined />} showIcon closable onClose={() => setShowDataSourceAlert(false)} style={{ marginBottom: 12 }} />}
            {showDataSourceAlert && dataSource === 'offline' && <Alert type="warning" message="Modo Offline" description="Los datos pueden no estar actualizados." icon={<WarningOutlined />} showIcon closable onClose={() => setShowDataSourceAlert(false)} style={{ marginBottom: 12 }} />}
            {syncError && dataSource !== 'backend' && <Alert type="error" message="Error de sincronización" description={syncError} showIcon closable onClose={() => setSyncError(null)} action={<Button size="small" danger onClick={loadOperationState}>Reintentar</Button>} style={{ marginBottom: 12 }} />}

            {/* ══════════════════════════════════════════════════════════
                ROW 1 — Controls Card + KPI Cards
            ══════════════════════════════════════════════════════════ */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>

                {/* ── Caja Controls Card (leftmost) ── */}
                <Col xs={24} sm={12} xl={6}>
                    <Card
                        size="small"
                        style={{
                            borderRadius: 10,
                            borderLeft: `4px solid ${isDayStarted ? '#52c41a' : '#d9d9d9'}`,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
                            height: '100%',
                        }}
                        bodyStyle={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}
                    >
                        {/* Label */}
                        <Text type="secondary" style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Caja Diaria
                        </Text>

                        {/* Date + Status tag on the same line */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                            {isDayStarted && dayStartTime ? (
                                <div style={{ fontSize: 28, fontWeight: 700, color: '#222', lineHeight: 1.1 }}>
                                    {dayjs(dayStartTime).format('DD/MM/YYYY')}
                                </div>
                            ) : (
                                <div />
                            )}
                            <Tag
                                icon={isDayStarted ? <CheckCircleOutlined /> : <CloseOutlined />}
                                color={isDayStarted ? 'success' : 'default'}
                                style={{ fontSize: 13, padding: '3px 10px', margin: 0, flexShrink: 0 }}
                            >
                                {isDayStarted ? 'Día Iniciado' : 'Caja Cerrada'}
                            </Tag>
                        </div>

                        {/* Spacer pushes buttons to bottom */}
                        <div style={{ flex: 1 }} />

                        {/* Action buttons — 2-column grid */}
                        {!isDayStarted ? (
                            <Button
                                type="primary"
                                icon={<PlayCircleOutlined />}
                                onClick={handleStartDay}
                                loading={loading}
                                block
                                style={{ borderRadius: 7 }}
                            >
                                Iniciar Día
                            </Button>
                        ) : (
                            <Row gutter={[6, 6]}>
                                <Col span={12}>
                                    <Button
                                        icon={<SwapOutlined />}
                                        onClick={() => setWithdrawalModalVisible(true)}
                                        block
                                        style={{ borderRadius: 7, fontSize: 12 }}
                                    >
                                        Retiro/Depósito
                                    </Button>
                                </Col>
                                <Col span={12}>
                                    <Button
                                        type="primary"
                                        danger
                                        icon={<LogoutOutlined />}
                                        onClick={handleCloseDay}
                                        block
                                        style={{ borderRadius: 7, fontSize: 12 }}
                                    >
                                        Cerrar
                                    </Button>
                                </Col>
                            </Row>
                        )}
                    </Card>
                </Col>

                {/* ── Fondo Inicial ── */}
                <Col xs={24} sm={12} xl={6}>
                    <Card
                        size="small"
                        style={{ borderRadius: 10, borderLeft: '4px solid #1890ff', boxShadow: '0 2px 8px rgba(0,0,0,0.07)', height: '100%' }}
                        bodyStyle={{ padding: '16px 20px' }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                                <Text type="secondary" style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Fondo Inicial</Text>
                                <div style={{ fontSize: 39, fontWeight: 700, color: '#1890ff', lineHeight: 1.2, marginTop: 4 }}>
                                    {`$ ${initialCash.toLocaleString('es-AR', { minimumFractionDigits: 0 })}`}
                                </div>
                                {isDayStarted && dayStartTime ? (
                                    <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 6 }}>
                                        <WifiOutlined style={{ marginRight: 4 }} />
                                        {dayjs(dayStartTime).format('DD/MM/YYYY HH:mm')}
                                        {openedBy ? ` · ${openedBy}` : ''}
                                    </Text>
                                ) : (
                                    <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 6 }}>Se define al iniciar el día</Text>
                                )}
                            </div>
                            {isDayStarted && (
                                <LockOutlined style={{ color: '#bbb', marginLeft: 8, marginTop: 4 }} />
                            )}
                        </div>
                    </Card>
                </Col>

                {/* ── Movimientos Netos ── */}
                <Col xs={24} sm={12} xl={6}>
                    <Card
                        size="small"
                        style={{ borderRadius: 10, borderLeft: `4px solid ${movementsNetTotal >= 0 ? '#52c41a' : '#ff4d4f'}`, boxShadow: '0 2px 8px rgba(0,0,0,0.07)', height: '100%' }}
                        bodyStyle={{ padding: '16px 20px' }}
                    >
                        <Text type="secondary" style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Movimientos Netos</Text>
                        {movementsLoading ? <Spin size="small" style={{ marginTop: 12 }} /> : (
                            <>
                                <div style={{ fontSize: 39, fontWeight: 700, color: movementsNetTotal >= 0 ? '#52c41a' : '#ff4d4f', lineHeight: 1.2, marginTop: 4 }}>
                                    {movementsNetTotal >= 0 ? '+' : '−'} ${Math.abs(movementsNetTotal).toLocaleString('es-AR', { minimumFractionDigits: 0 })}
                                </div>
                                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 6 }}>
                                    {movementsNetTotal >= 0
                                        ? <ArrowUpOutlined style={{ marginRight: 4, color: '#52c41a' }} />
                                        : <ArrowDownOutlined style={{ marginRight: 4, color: '#ff4d4f' }} />}
                                    {movements.length} {movements.length === 1 ? 'movimiento' : 'movimientos'}
                                </Text>
                            </>
                        )}
                    </Card>
                </Col>

                {/* ── Ventas en Efectivo ── */}
                <Col xs={24} sm={12} xl={6}>
                    <Card
                        size="small"
                        style={{ borderRadius: 10, borderLeft: '4px solid #722ed1', boxShadow: '0 2px 8px rgba(0,0,0,0.07)', height: '100%' }}
                        bodyStyle={{ padding: '16px 20px' }}
                    >
                        <Text type="secondary" style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Ventas en Efectivo</Text>
                        {salesLoading ? <Spin size="small" style={{ marginTop: 12 }} /> : (
                            <>
                                <div style={{ fontSize: 39, fontWeight: 700, color: '#722ed1', lineHeight: 1.2, marginTop: 4 }}>
                                    {`$ ${cashData.totalAmount.toLocaleString('es-AR', { minimumFractionDigits: 0 })}`}
                                </div>
                                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 6 }}>
                                    <CheckCircleOutlined style={{ marginRight: 4 }} />
                                    {cashData.invoiceCount} {cashData.invoiceCount === 1 ? 'factura' : 'facturas'}
                                </Text>
                            </>
                        )}
                    </Card>
                </Col>

            </Row>

            {/* ══════════════════════════════════════════════════════════
                ROW 3 — Movimientos del Día (full-width table)
            ══════════════════════════════════════════════════════════ */}
            <Card
                size="small"
                title={
                    <Space>
                        <DollarOutlined />
                        <span>Movimientos del Día</span>
                        {movements.length > 0 && (
                            <Tag color={movementsNetTotal >= 0 ? 'green' : 'red'} style={{ marginLeft: 4 }}>
                                {movementsNetTotal >= 0 ? '+' : '−'} ${Math.abs(movementsNetTotal).toLocaleString('es-AR', { minimumFractionDigits: 0 })} neto
                            </Tag>
                        )}
                    </Space>
                }
                style={{ borderRadius: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.07)' }}
            >
                {!isDayStarted ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<Text type="secondary">Inicie el día para ver los movimientos</Text>} style={{ padding: '32px 0' }} />
                ) : (
                    <div
                        style={
                            movements.length > 6
                                ? { maxHeight: 360, overflowY: 'auto', overflowX: 'hidden' }
                                : { overflowX: 'auto' }
                        }
                    >
                        <Table
                            columns={movementColumns}
                            dataSource={movements}
                            rowKey={(r, i) => r.movementId || r.id || i}
                            loading={movementsLoading}
                            pagination={movements.length > 20 ? { pageSize: 20, size: 'small', showSizeChanger: false } : false}
                            size="middle"
                            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Sin movimientos registrados hoy" style={{ padding: '24px 0' }} /> }}
                            rowClassName={(r) => {
                                const type = r.movementType || r.type || '';
                                return (type === 'income' || type === 'payment') ? 'row-income' : 'row-outflow';
                            }}
                            style={{ fontSize: 13 }}
                        />
                    </div>
                )}
            </Card>

            {/* ── Close Day Modal ── */}
            <Modal
                title={<Space><LogoutOutlined style={{ color: '#ff4d4f' }} /><span>Cerrar Caja</span></Space>}
                open={closeDayModalVisible}
                onCancel={() => setCloseDayModalVisible(false)}
                footer={[
                    <Button key="cancel" onClick={() => setCloseDayModalVisible(false)}>Cancelar</Button>,
                    <Button key="confirm" type="primary" danger icon={<LogoutOutlined />} onClick={handleConfirmCloseDay} disabled={isDemoMode || modalTotalCounted === null || modalTotalCounted === undefined}>Cerrar Caja</Button>
                ]}
                width={500}
            >
                <div style={{ padding: '16px 0' }}>
                    {loadingSummary ? (
                        <div style={{ textAlign: 'center', padding: '40px 0' }}><Spin size="large" tip="Calculando resumen..." /></div>
                    ) : closingSummary ? (
                        <>
                            <Text strong style={{ display: 'block', marginBottom: 12 }}>Resumen del Día</Text>
                            <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
                                {[
                                    { label: 'Fondo Inicial', value: closingSummary.initialCash, color: '#1890ff', bg: '#f0f5ff' },
                                    { label: 'Ventas en Efectivo', value: closingSummary.cashSales, color: '#52c41a', bg: '#f6ffed', prefix: '+' },
                                    ...(closingSummary.income > 0 ? [{ label: 'Ingresos Manuales', value: closingSummary.income, color: '#52c41a', bg: '#f6ffed', prefix: '+' }] : []),
                                    { label: 'Retiros', value: closingSummary.withdrawals, color: '#ff4d4f', bg: '#fff1f0', prefix: '−' },
                                    ...(closingSummary.expenses > 0 ? [{ label: 'Gastos', value: closingSummary.expenses, color: '#ff4d4f', bg: '#fff1f0', prefix: '−' }] : []),
                                ].map(({ label, value, color, bg, prefix = '' }) => (
                                    <Col span={12} key={label}>
                                        <div style={{ textAlign: 'center', padding: '10px 8px', backgroundColor: bg, borderRadius: 8 }}>
                                            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>{label}</Text>
                                            <Text strong style={{ fontSize: 16, color }}>{prefix}${(value || 0).toFixed(2)}</Text>
                                        </div>
                                    </Col>
                                ))}
                                <Col span={24}>
                                    <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#f5f0ff', borderRadius: 8, border: '1px solid #d3adf7' }}>
                                        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Total Esperado en Caja</Text>
                                        <Text strong style={{ fontSize: 22, color: '#722ed1' }}>${(closingSummary.expectedCash || 0).toFixed(2)}</Text>
                                    </div>
                                </Col>
                            </Row>
                        </>
                    ) : (
                        <Alert type="error" message="No se pudo cargar el resumen" description="Intente nuevamente" style={{ marginBottom: 20 }} />
                    )}
                    <Divider style={{ margin: '16px 0' }} />
                    <div style={{ marginBottom: 16 }}>
                        <Text strong style={{ display: 'block', marginBottom: 4 }}>Total Contado</Text>
                        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>Ingrese el efectivo contado al cierre de caja</Text>
                        <InputNumber
                            prefix="$" value={modalTotalCounted}
                            onChange={(val) => { setModalTotalCounted(val); if (val !== null && closingSummary) setModalDifference(val - closingSummary.expectedCash); else setModalDifference(null); }}
                            style={{ width: '100%' }} precision={2} min={0} size="large" placeholder="Ingresar efectivo contado" autoFocus
                        />
                    </div>
                    {modalDifference !== null && (
                        <div style={{ padding: 16, backgroundColor: modalDifference === 0 ? '#f6ffed' : modalDifference > 0 ? '#e6f7ff' : '#fff1f0', border: `1px solid ${modalDifference === 0 ? '#b7eb8f' : modalDifference > 0 ? '#91d5ff' : '#ffa39e'}`, borderRadius: 8, textAlign: 'center' }}>
                            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Descuadre</Text>
                            <Text strong style={{ fontSize: 26, color: modalDifference > 0 ? '#52c41a' : modalDifference < 0 ? '#ff4d4f' : '#52c41a' }}>
                                {modalDifference >= 0 ? '+' : ''}${modalDifference.toFixed(2)}
                            </Text>
                            <br />
                            <Text type={modalDifference === 0 ? 'success' : 'warning'} style={{ fontSize: 12 }}>
                                {modalDifference === 0 ? '✓ Caja cuadrada' : modalDifference > 0 ? '↑ Sobrante en caja' : '↓ Faltante en caja'}
                            </Text>
                        </div>
                    )}
                    <Divider style={{ margin: '16px 0' }} />
                    <Text type="secondary" style={{ fontSize: 11, display: 'block', textAlign: 'center' }}>Esta acción cerrará el día y generará un reporte</Text>
                </div>
            </Modal>

            {/* ── Iniciar Día Modal ── */}
            <Modal
                title={<Space><PlayCircleOutlined style={{ color: '#1890ff' }} /><span>Iniciar Día</span></Space>}
                open={startDayModalVisible}
                onCancel={() => setStartDayModalVisible(false)}
                onOk={handleConfirmStartDay}
                okText="Iniciar Día"
                cancelText="Cancelar"
                confirmLoading={loading}
                okButtonProps={{ icon: <PlayCircleOutlined /> }}
                width={360}
                destroyOnClose
            >
                <div style={{ padding: '8px 0' }}>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 16 }}>
                        Ingrese el fondo en efectivo disponible al inicio del día.
                    </Text>
                    <div style={{ marginBottom: 16 }}>
                        <Text strong style={{ display: 'block', marginBottom: 6 }}>Fondo Inicial</Text>
                        <InputNumber
                            prefix="$"
                            value={tempInitialCash}
                            onChange={(v) => setTempInitialCash(v ?? 0)}
                            style={{ width: '100%' }}
                            precision={2}
                            min={0}
                            size="large"
                            autoFocus
                            formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                            parser={(v) => v.replace(/\./g, '')}
                        />
                    </div>
                    <div style={{ background: '#f5f5f5', borderRadius: 6, padding: '10px 14px' }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Fecha / Hora: <strong>{dayjs().format('DD/MM/YYYY HH:mm:ss')}</strong>
                        </Text>
                    </div>
                </div>
            </Modal>

            {/* ── Withdrawal Modal ── */}
            <CashWithdrawal visible={withdrawalModalVisible} onClose={() => setWithdrawalModalVisible(false)} onSuccess={() => loadMovementsData()} />
        </>
    );
}

// ─── REPORTES TAB ─────────────────────────────────────────────────────────────
function ReportesTab() {
    const [operations, setOperations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
    const [filters, setFilters] = useState({ startDate: null, endDate: null, status: 'closed' });
    const [summary, setSummary] = useState({ byWeek: [], byMonth: [], overall: {} });
    const [summaryPeriod, setSummaryPeriod] = useState('overall');
    const [reportModalVisible, setReportModalVisible] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const printRef = useRef();

    const fetchOperations = async (page = 1, pageSize = 20) => {
        setLoading(true);
        try {
            const params = { page, limit: pageSize, status: filters.status };
            if (filters.startDate) params.startDate = filters.startDate;
            if (filters.endDate) params.endDate = filters.endDate;
            const res = await operationsService.getOperations(params);
            if (res?.success) {
                const ops = res.data.operations || [];
                setOperations(ops);
                setPagination({ current: res.data.pagination?.page || page, pageSize: res.data.pagination?.limit || pageSize, total: res.data.pagination?.total || 0 });
                if (res.data.summary) setSummary(res.data.summary);
            } else {
                message.error('Error al cargar reportes');
            }
        } catch (_) {
            message.error('Error al cargar reportes del servidor');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchOperations(); }, []); // eslint-disable-line

    // ── Derived summary values based on period selector ──
    const week   = summary.byWeek?.[0]  || {};
    const month  = summary.byMonth?.[0] || {};
    const all    = summary.overall       || {};

    const periodLabel = summaryPeriod === 'week'
        ? (week.weekStart ? `Semana ${dayjs.utc(week.weekStart).format('DD/MM')} – ${dayjs.utc(week.weekEnd).format('DD/MM')}` : 'Semana actual')
        : summaryPeriod === 'month'
            ? (month.monthName ? `${month.monthName} ${month.year}` : 'Mes actual')
            : 'Total general';

    const derivedStats = {
        operationsCount: summaryPeriod === 'week' ? (week.operationsCount ?? 0)
            : summaryPeriod === 'month' ? (month.operationsCount ?? 0)
            : (all.totalClosedOperations ?? 0),
        surplus: summaryPeriod === 'week' ? (week.surplus ?? 0)
            : summaryPeriod === 'month' ? (month.surplus ?? 0)
            : (all.totalSurplus ?? 0),
        shortage: summaryPeriod === 'week' ? (week.shortage ?? 0)
            : summaryPeriod === 'month' ? (month.shortage ?? 0)
            : (all.totalShortage ?? 0),
        net: summaryPeriod === 'week' ? (week.net ?? 0)
            : summaryPeriod === 'month' ? (month.net ?? 0)
            : (all.totalNet ?? 0),
    };

    const getVarianceColor = (v) => { if (!v || v === 0) return 'green'; if (Math.abs(v) < 50) return 'gold'; return v > 0 ? 'blue' : 'red'; };
    const getVarianceText  = (v) => { if (!v || v === 0) return 'Exacto'; if (v > 0) return `+${fmt(v)}`; return fmt(v); };

    const handlePrintReport = () => {
        const win = window.open('', '_blank');
        if (win && printRef.current) {
            win.document.write(`<!DOCTYPE html><html><head><title>Reporte ${dayjs(selectedDate).format('DD/MM/YYYY')}</title><style>body{font-family:sans-serif;margin:20px}table{width:100%;border-collapse:collapse}th,td{border:1px solid #ddd;padding:8px}th{background:#f5f5f5}</style></head><body>${printRef.current.innerHTML}<script>window.onload=function(){window.print();window.onafterprint=function(){window.close();};}</script></body></html>`);
            win.document.close();
        }
    };

    const columns = [
        { title: 'ID', dataIndex: 'operationId', key: 'operationId', width: 70 },
        { title: 'Fecha', dataIndex: 'operationDate', key: 'operationDate', width: 120, render: (d) => d ? dayjs.utc(d).format('DD/MM/YYYY') : 'N/A' },
        { title: 'Apertura', dataIndex: 'openedAt', key: 'openedAt', width: 90, render: (t) => t ? dayjs.utc(t).local().format('HH:mm') : 'N/A' },
        { title: 'Cierre', dataIndex: 'closedAt', key: 'closedAt', width: 90, render: (t) => t ? dayjs.utc(t).local().format('HH:mm') : 'N/A' },
        { title: 'Fondo Inicial', dataIndex: 'initialCash', key: 'initialCash', width: 130, align: 'right', render: fmt },
        { title: 'Esperado', dataIndex: 'expectedCash', key: 'expectedCash', width: 130, align: 'right', render: fmt },
        { title: 'Contado', dataIndex: 'countedCash', key: 'countedCash', width: 130, align: 'right', render: fmt },
        {
            title: 'Diferencia', dataIndex: 'variance', key: 'variance', width: 120, align: 'right',
            render: (v) => <Tag color={getVarianceColor(v)}>{getVarianceText(v)}</Tag>
        },
        {
            title: 'Estado', dataIndex: 'status', key: 'status', width: 100,
            render: (s) => {
                const map = { open: ['blue', 'Abierto'], closed: ['green', 'Cerrado'], suspended: ['orange', 'Suspendido'] };
                const [color, label] = map[s] || ['default', s];
                return <Tag color={color}>{label}</Tag>;
            }
        },
        {
            title: 'Acciones', key: 'actions', width: 90, fixed: 'right',
            render: (_, record) => (
                <Tooltip title="Ver Reporte">
                    <Button type="primary" size="small" icon={<EyeOutlined />}
                        onClick={() => {
                            if (isDemoMode) {
                                message.info('Informes Detallados con todos los movimientos de Caja del Día');
                                return;
                            }
                            setSelectedDate(dayjs.utc(record.operationDate).format('YYYY-MM-DD'));
                            setReportModalVisible(true);
                        }}
                    />
                </Tooltip>
            )
        },
    ];

    return (
        <div>
            {/* ── Summary Cards ── */}
            <Card
                size="small"
                style={{ marginBottom: 16, borderRadius: 8 }}
                bodyStyle={{ padding: '12px 20px' }}
            >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {periodLabel}
                    </Text>
                    <Segmented
                        size="small"
                        value={summaryPeriod}
                        onChange={setSummaryPeriod}
                        options={[
                            { label: 'Esta semana', value: 'week',    disabled: isDemoMode },
                            { label: 'Este mes',    value: 'month',   disabled: isDemoMode },
                            { label: 'Total',       value: 'overall' },
                        ]}
                    />
                </div>
            </Card>

            <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                {/* TOTAL OPERACIONES */}
                <Col span={6}>
                    <Card size="small" loading={loading} style={{ borderLeft: '4px solid #1890ff', borderRadius: 8, boxShadow: '0 2px 6px rgba(0,0,0,0.06)', height: '100%' }}>
                        <Text type="secondary" style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: 8 }}>Total Operaciones</Text>
                        <div style={{ fontSize: 36, fontWeight: 700, color: '#1890ff', lineHeight: 1 }}>
                            {derivedStats.operationsCount}
                        </div>
                        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 6 }}>
                            <CalendarOutlined style={{ marginRight: 4 }} />
                            {summaryPeriod === 'week' ? 'esta semana' : summaryPeriod === 'month' ? 'este mes' : `de ${all.totalClosedOperations ?? 0} cerradas / ${all.totalOperations ?? 0} totales`}
                        </Text>
                    </Card>
                </Col>

                {/* SOBRANTES */}
                <Col span={6}>
                    <Card size="small" loading={loading} style={{ borderLeft: '4px solid #3f8600', borderRadius: 8, boxShadow: '0 2px 6px rgba(0,0,0,0.06)', height: '100%' }}>
                        <Text type="secondary" style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: 8 }}>Sobrantes</Text>
                        <div style={{ fontSize: 36, fontWeight: 700, color: '#3f8600', lineHeight: 1 }}>
                            {`$ ${(derivedStats.surplus || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                        </div>
                        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 6 }}>
                            <ArrowUpOutlined style={{ marginRight: 4, color: '#3f8600' }} />
                            excedente de efectivo
                        </Text>
                    </Card>
                </Col>

                {/* FALTANTES */}
                <Col span={6}>
                    <Card size="small" loading={loading} style={{ borderLeft: '4px solid #cf1322', borderRadius: 8, boxShadow: '0 2px 6px rgba(0,0,0,0.06)', height: '100%' }}>
                        <Text type="secondary" style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: 8 }}>Faltantes</Text>
                        <div style={{ fontSize: 36, fontWeight: 700, color: '#cf1322', lineHeight: 1 }}>
                            {`$ ${(derivedStats.shortage || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                        </div>
                        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 6 }}>
                            <ArrowDownOutlined style={{ marginRight: 4, color: '#cf1322' }} />
                            déficit de efectivo
                        </Text>
                    </Card>
                </Col>

                {/* DIFERENCIA TOTAL */}
                <Col span={6}>
                    <Card size="small" loading={loading} style={{ borderLeft: `4px solid ${derivedStats.net >= 0 ? '#3f8600' : '#cf1322'}`, borderRadius: 8, boxShadow: '0 2px 6px rgba(0,0,0,0.06)', height: '100%' }}>
                        <Text type="secondary" style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: 8 }}>Diferencia Total</Text>
                        <div style={{ fontSize: 36, fontWeight: 700, color: derivedStats.net >= 0 ? '#3f8600' : '#cf1322', lineHeight: 1 }}>
                            {`${derivedStats.net >= 0 ? '+' : ''}$ ${(derivedStats.net || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                        </div>
                        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 6 }}>
                            <DollarOutlined style={{ marginRight: 4 }} />
                            sobrantes − faltantes
                        </Text>
                    </Card>
                </Col>
            </Row>

            {/* Filters */}
            <Card size="small" style={{ marginBottom: 16, borderRadius: 8 }}>
                <Space wrap size="middle">
                    <RangePicker
                        format="DD/MM/YYYY"
                        placeholder={['Fecha desde', 'Fecha hasta']}
                        disabled={isDemoMode}
                        onChange={(dates) => {
                            if (dates) { setFilters(f => ({ ...f, startDate: dates[0].format('YYYY-MM-DD'), endDate: dates[1].format('YYYY-MM-DD') })); }
                            else { setFilters(f => ({ ...f, startDate: null, endDate: null })); }
                        }}
                    />
                    <Button type="primary" disabled={isDemoMode} onClick={() => fetchOperations(1, pagination.pageSize)}>Aplicar Filtros</Button>
                    <Button disabled={isDemoMode} onClick={() => { setFilters({ startDate: null, endDate: null, status: 'closed' }); setTimeout(() => fetchOperations(1, pagination.pageSize), 100); }}>Limpiar</Button>
                    <Button icon={<ReloadOutlined />} disabled={isDemoMode} onClick={() => fetchOperations(pagination.current, pagination.pageSize)}>Actualizar</Button>
                </Space>
            </Card>

            {/* Table */}
            <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.07)', padding: '16px 20px' }}>
                <Table
                    columns={columns}
                    dataSource={operations}
                    rowKey="operationId"
                    loading={loading}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50', '100'],
                        showTotal: (total, range) => `${range[0]}–${range[1]} de ${total} operaciones`,
                        locale: { items_per_page: '/ pág.' },
                    }}
                    onChange={(p) => fetchOperations(p.current, p.pageSize)}
                    scroll={{ x: 1100 }}
                    size="middle"
                />
            </div>

            {/* Report Modal */}
            <Modal
                title={<span><CalendarOutlined style={{ marginRight: 8 }} />Reporte — {selectedDate ? dayjs(selectedDate).format('DD/MM/YYYY') : ''}</span>}
                open={reportModalVisible}
                onCancel={() => { setReportModalVisible(false); setSelectedDate(null); }}
                footer={[
                    <Button key="close" onClick={() => setReportModalVisible(false)} icon={<CloseOutlined />}>Cerrar</Button>,
                    <Button key="print" type="primary" icon={<PrinterOutlined />} onClick={handlePrintReport}>Imprimir</Button>
                ]}
                width={1200}
                style={{ top: 20 }}
                styles={{ body: { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' } }}
            >
                <div ref={printRef}>
                    {selectedDate && <OperationReport dateOverride={selectedDate} />}
                </div>
            </Modal>
        </div>
    );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
const CajaPage = () => {
    const [reloadKey, setReloadKey] = useState(0);
    const [isReloading, setIsReloading] = useState(false);

    const handleReload = () => {
        setIsReloading(true);
        setReloadKey(prev => prev + 1);
        setTimeout(() => setIsReloading(false), 600);
    };

    const baseItems = [
        {
            key: 'caja',
            label: <span><DollarOutlined /> Caja Diaria</span>,
            children: (
                <div className="caja-fixed-tab">
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
                        <Tooltip title="Actualizar datos de ventas">
                            <Button type="primary" icon={<ReloadOutlined spin={isReloading} />} onClick={handleReload} loading={isReloading}>
                                Actualizar
                            </Button>
                        </Tooltip>
                    </div>
                    <CajaDiariaTab reloadKey={reloadKey} />
                </div>
            ),
        },
        {
            key: 'reportes',
            label: <span><CalendarOutlined /> Reportes</span>,
            children: <div className="caja-scrollable-tab"><ReportesTab /></div>,
        },
        {
            key: 'mercadopago',
            label: <span><CreditCardOutlined /> Mercado Pago</span>,
            children: (
                <div className="caja-scrollable-tab" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, padding: 40 }}>
                    <Card style={{ textAlign: 'center', backgroundColor: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 12, maxWidth: 400 }}>
                        <ToolOutlined style={{ fontSize: 48, color: '#faad14', marginBottom: 16 }} />
                        <Text strong style={{ fontSize: 18, display: 'block', marginBottom: 8 }}>Funcionalidad en Desarrollo</Text>
                        <Text type="secondary">La integración con Mercado Pago estará disponible próximamente.</Text>
                    </Card>
                </div>
            ),
        },
    ];
    const items = isDemoMode ? baseItems.filter(item => item.key !== 'mercadopago') : baseItems;

    return (
        <div className="caja-container">
            <Tabs
                defaultActiveKey="caja"
                items={items}
                size="large"
                type="card"
                className="caja-tabs"
            />
        </div>
    );
};

export default CajaPage;
